import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, type EpidemicStatus, type EpidemicTrack } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const PAGE = 8;

type Props = {
  music?: EpidemicStatus | null;
  selectedId?: string;
  selectedLabel?: string;
  onSelect: (track: EpidemicTrack | null) => void;
  defaultQuery?: string;
  allowClear?: boolean;
  className?: string;
};

function mergeTracks(prev: EpidemicTrack[], next: EpidemicTrack[]) {
  const seen = new Set(prev.map((t) => t.id));
  const out = [...prev];
  for (const row of next) {
    if (!row.id || seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

export default function EpidemicMusicPicker({
  music,
  selectedId = "",
  selectedLabel = "",
  onSelect,
  defaultQuery = "warm calm piano soft instrumental",
  allowClear = false,
  className,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [q, setQ] = useState(defaultQuery);
  const [mood, setMood] = useState("");
  const [genre, setGenre] = useState("");
  const [tracks, setTracks] = useState<EpidemicTrack[]>([]);
  const [term, setTerm] = useState("");
  const [previewId, setPreviewId] = useState(selectedId);
  const [hasMore, setHasMore] = useState(false);

  const moodsQ = useQuery({
    queryKey: ["carousel-music-moods"],
    queryFn: api.carouselMusicMoods,
    staleTime: 300_000,
    retry: false,
    enabled: music?.can_search !== false,
  });
  const genresQ = useQuery({
    queryKey: ["carousel-music-genres"],
    queryFn: api.carouselMusicGenres,
    staleTime: 300_000,
    retry: false,
    enabled: music?.can_search !== false,
  });

  const fetchPage = (offset: number) =>
    q.trim()
      ? api.carouselMusicSearch(q, PAGE, { mood, genre, offset })
      : api.carouselMusicBrowse({ limit: PAGE, offset, mood, genre });

  const searchM = useMutation({
    mutationFn: () => fetchPage(0),
    onSuccess: (data) => {
      setTracks(data.tracks || []);
      setHasMore(Boolean(data.has_more ?? (data.tracks || []).length >= PAGE));
      setTerm(data.term || (mood || genre ? `filtry: ${[mood, genre].filter(Boolean).join(" · ")}` : ""));
    },
  });

  const moreM = useMutation({
    mutationFn: () => fetchPage(tracks.length),
    onSuccess: (data) => {
      const batch = data.tracks || [];
      setTracks((prev) => mergeTracks(prev, batch));
      setHasMore(Boolean(data.has_more ?? batch.length >= PAGE));
    },
  });

  const selected = useMemo(
    () => tracks.find((t) => t.id === selectedId) || null,
    [tracks, selectedId],
  );
  const activePreviewId = previewId || selectedId;
  const canSearch = music?.can_search !== false && music?.configured !== false;
  const canMux = music?.can_download !== false && music?.state !== "missing_key";
  const showLoadMore = tracks.length > 0 && !selectedId && hasMore;
  const busy = searchM.isPending || moreM.isPending;

  const playTrack = (track: EpidemicTrack) => {
    onSelect(track);
    setPreviewId(track.id);
    const el = audioRef.current;
    if (!el) return;
    const src = api.carouselMusicPreview(track.id);
    if (el.getAttribute("src") !== src) {
      el.setAttribute("src", src);
      el.load();
    }
    el.currentTime = 0;
    void el.play().catch(() => undefined);
  };

  const clearTrack = () => {
    onSelect(null);
    setPreviewId("");
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    el.removeAttribute("src");
    el.load();
  };

  return (
    <div className={cn("space-y-2", className)}>
      {music?.hint ? <p className="text-xs text-warning">{music.hint}</p> : null}
      {!canMux ? (
        <p className="text-xs text-warning">
          Epidemic nie pozwala jeszcze na pobranie MP3 (mux). Search/podgląd mogą działać.
        </p>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          Download OK. Klik w utwór = wybór + od razu podgląd.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="np. warm calm piano"
          className="max-w-xs"
          onKeyDown={(e) => {
            if (e.key === "Enter") searchM.mutate();
          }}
        />
        <select
          className="rounded border px-1.5 py-1 text-xs"
          value={mood}
          onChange={(e) => setMood(e.target.value)}
        >
          <option value="">mood (opcjonalnie)</option>
          {(moodsQ.data?.items || []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.label}
            </option>
          ))}
        </select>
        <select
          className="rounded border px-1.5 py-1 text-xs"
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
        >
          <option value="">genre (opcjonalnie)</option>
          {(genresQ.data?.items || []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.label}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !canSearch || (!q.trim() && !mood && !genre)}
          onClick={() => searchM.mutate()}
        >
          {searchM.isPending ? "Szukam…" : q.trim() ? "Szukaj" : "Przeglądaj"}
        </Button>
        {allowClear && selectedId ? (
          <Button size="sm" variant="ghost" onClick={clearTrack}>
            Wyczyść Epidemic
          </Button>
        ) : null}
      </div>
      {term ? <p className="text-[11px] text-muted-foreground">Query: {term}</p> : null}
      {searchM.isError || moreM.isError ? (
        <p className="text-xs text-destructive">
          {((searchM.error || moreM.error) as Error)?.message || "błąd search"}
        </p>
      ) : null}
      {(selectedLabel || selected) && selectedId ? (
        <p className="text-xs text-foreground">
          Wybrano: {selected ? `${selected.title} · ${selected.artist}` : selectedLabel || selectedId}
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        {tracks.map((track) => (
          <button
            key={track.id}
            type="button"
            onClick={() => playTrack(track)}
            className={cn(
              "rounded-md border p-2 text-left text-xs",
              selectedId === track.id ? "border-brand bg-brand/5" : "border-border",
            )}
          >
            <div className="font-medium text-foreground">{track.title || track.id}</div>
            <div className="text-muted-foreground">{track.artist || "Epidemic"}</div>
            {track.length_sec ? (
              <div className="mt-0.5 text-[10px] text-muted-foreground">{track.length_sec}s</div>
            ) : null}
          </button>
        ))}
      </div>
      {showLoadMore ? (
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !canSearch}
          onClick={() => moreM.mutate()}
        >
          {moreM.isPending ? "Ładuję…" : "Załaduj kolejne"}
        </Button>
      ) : null}
      <audio
        ref={audioRef}
        className={cn("w-full", activePreviewId ? "block" : "hidden")}
        controls
        preload="none"
      />
    </div>
  );
}
